import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, take, takeUntil, tap } from 'rxjs';

import { AuthService } from '@core/services/auth.service';
import { LookupService } from '@core/services/lookup.service';
import { Role } from '@core/enums/roles';
import { IDistrict } from '@core/interfaces/ilookup';
import { ISchoolYear } from '@core/interfaces/ischool-year';
import { ReportService } from '../services/report.service';
import { GraphType } from '../enums/report-graph';
import { IReportCardRequest, IReportFilters } from '../interfaces/ireport-request';
import {
  IReportCard,
  IReportCardDistrictCount,
  IReportCardGraph,
  IReportCardSchoolCount,
} from '../interfaces/ireport-card';

/**
 * Reports page. Single owner of the filter state.
 *
 *   ReportCardsComponent (this)
 *   ├── prism-report-card      Total Districts / Total Schools
 *   ├── prism-report-card      Total Completions
 *   ├── prism-report-filters   search, academic year, district, completion status
 *   └── prism-report-results   paged grid + download
 *
 * The form lives here. Cards, filters and results all read from it, so the
 * card totals and the grid always describe the same slice of data.
 */
@Component({
  selector: 'prism-report-cards',
  templateUrl: './report-cards.component.html',
  styleUrl: './report-cards.component.scss',
})
export class ReportCardsComponent implements OnInit, OnDestroy {
  private reportService = inject(ReportService);
  private authService = inject(AuthService);
  private lookupService = inject(LookupService);
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();

  user = this.authService.getCurrentUser();
  graphType = GraphType;

  filterForm!: FormGroup;
  /** Immutable snapshot passed to children. Replaced (not mutated) so OnChanges fires. */
  filters!: IReportFilters;

  organizationOptions: IDistrict[] = [];
  completionStatusOptions = [
    { id: 'Completed', name: 'Completed' },
    { id: 'Incomplete', name: 'Incomplete' },
    { id: 'Not Started', name: 'Not Started' },
  ];

  /** Real current school year, used for the cards when the year filter is "All". */
  currentSchoolYearId: number | null = null;

  districtCardData: IReportCardGraph = {};
  completionsCardData: IReportCardGraph = {};
  isDistrictLoading = false;
  isCompletionsLoading = false;

  /**
   * District id the cards should use.
   *
   * A platform admin is scoped ONLY by the filter, so "All districts" means
   * platform-wide. We must not fall back to authService.getCurrentDistrict(),
   * which is a localStorage value left over from other pages and would silently
   * scope the cards to a district the filter is not showing.
   *
   * A district admin has no platform-wide view, so their own district stands in
   * when the filter is empty.
   */
  get cardDistrictId(): number | null {
    if (this.filters.organization != null) return this.filters.organization;
    if (this.user?.roleId === Role.SystemAdmin) return null;
    return this.authService.getCurrentDistrict()?.id ?? null;
  }
  get cardSchoolYearId(): number | null {
    return this.filters.schoolYearId ?? this.currentSchoolYearId;
  }

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      search: [''],
      schoolYearId: [null],
      organization: [null],
      courseId: [null],
      completionStatus: [null],
    });
    this.filters = this.snapshot();

    this.loadOrganizations();
    this.listenToFilterChanges();

    this.lookupService.getCurrentSchoolYear().pipe(take(1)).subscribe((res: ISchoolYear) => {
      this.currentSchoolYearId = res.id;
      this.lookupService.schoolYearSubject$.next(res.id);
      this.refreshCards();
    });
  }

  private snapshot(): IReportFilters {
    const v = this.filterForm.value;
    return {
      search: (v.search ?? '').trim(),
      schoolYearId: v.schoolYearId ?? null,
      organization: v.organization ?? null,
      courseId: v.courseId ?? null,
      completionStatus: v.completionStatus || null,
    };
  }

  private loadOrganizations(): void {
    this.lookupService.getOrganizations().pipe(take(1)).subscribe((districts: IDistrict[]) => {
      this.organizationOptions = districts;
    });
  }

  /**
   * Dropdown changes apply immediately. The text box applies on explicit search
   * (button / enter) via onSearch(), so typing does not spam the API.
   */
  private listenToFilterChanges(): void {
    const controls = ['schoolYearId', 'organization', 'courseId', 'completionStatus'];
    controls.forEach(name => {
      this.filterForm.get(name)!.valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(0),
        tap(() => this.applyFilters()),
        takeUntil(this.destroy$)
      ).subscribe();
    });
  }

  onSearch(): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    const next = this.snapshot();
    const districtChanged = next.organization !== this.filters.organization;
    const yearChanged = next.schoolYearId !== this.filters.schoolYearId;
    this.filters = next;

    // Keep the shared lookup subjects in sync for anything else that listens
    // (e.g. the card's "See all" navigation uses the current district).
    if (districtChanged) {
      const district = this.organizationOptions.find(o => o.id === next.organization) ?? null;
      this.authService.setCurrentDistrict(district);
      this.lookupService.districtSubject$.next(district);
    }
    if (yearChanged) {
      this.lookupService.schoolYearSubject$.next(this.cardSchoolYearId);
    }

    if (districtChanged || yearChanged) {
      this.refreshCards();
    }
  }

  // ---------------------------------------------------------------- cards

  private refreshCards(): void {
    const districtId = this.cardDistrictId;
    this.getDistrictData(districtId);
    // Pass the raw filter value, NOT cardSchoolYearId. A null year must reach the
    // proc as null so the card uses the same "current year + prior-year
    // certificates" rule the results grid uses. Substituting the current year
    // here made the card ignore prior-year completions the grid was showing.
    this.getTotalCompletions(districtId, this.filters.schoolYearId);
  }

  /**
   * Platform admins always see Total Districts. A district admin sees Total Schools
   * for their own district. Both series are cumulative counts by month of the
   * current school year.
   */
  private getDistrictData(districtId: number | null): void {
    this.isDistrictLoading = true;
    if (this.user?.roleId === Role.DistrictAdmin && districtId) {
      this.reportService.getSchoolCountWithoutGlobalLoading(districtId).pipe(take(1))
        .subscribe({
          next: (res: IReportCardSchoolCount[]) => {
            this.districtCardData = {
              total: res[0]?.totalSchoolCount ?? 0,
              labels: res.map(item => item.monthName),
              data: res.map(item => item.schoolCount),
              type: 'schools',
              title: 'Total Schools',
            };
            this.isDistrictLoading = false;
          },
          error: () => (this.isDistrictLoading = false),
        });
    } else {
      this.reportService.getDistrictCountWithoutGlobalLoading().pipe(take(1))
        .subscribe({
          next: (res: IReportCardDistrictCount[]) => {
            this.districtCardData = {
              total: res[0]?.totalDistrictCount ?? 0,
              labels: res.map(item => item.monthName),
              data: res.map(item => item.districtCount),
              type: 'districts',
              title: 'Total Districts',
            };
            this.isDistrictLoading = false;
          },
          error: () => (this.isDistrictLoading = false),
        });
    }
  }

  private getTotalCompletions(districtId: number | null, schoolYearId: number | null): void {
    this.isCompletionsLoading = true;
    const payload: IReportCardRequest = { isCompleted: true, districtId, schoolYearId };
    this.reportService.getCardInfoWithoutGlobalLoading(payload).pipe(take(1))
      .subscribe({
        next: (res: IReportCard) => {
          const pct = Math.round((res.progressPercent ?? 0) * 100) / 100;
          this.completionsCardData = {
            total: res.totalCompletedCourses,
            labels: ['Completion', 'Incomplete'],
            data: [pct, Math.round((100 - pct) * 100) / 100],
            type: 'completions',
            title: 'Total Completions',
          };
          this.isCompletionsLoading = false;
        },
        error: () => (this.isCompletionsLoading = false),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
